import type { InputEvent, InputProvider } from './InputProvider';
import type { ButtonDef } from './MouseInput';

export class TouchInput implements InputProvider {
  readonly name = 'touch';
  onInput: ((event: InputEvent) => void) | null = null;

  private _element: HTMLElement | null = null;
  private _suspended = false;
  private _buttonDefs: ButtonDef[];
  private _scale: number;
  private _touchPoints: Map<number, string> = new Map();
  private _pressedButtons: Set<string> = new Set();

  private _handleTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    if (this._suspended || !this._element) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const { x, y } = this._touchPos(touch);
      const hit = this._hitTest(x, y);
      if (hit) {
        this._touchPoints.set(touch.identifier, hit.keyCode);
        this._press(hit.keyCode);
      }
    }
  };

  private _handleTouchEnd = (e: TouchEvent): void => {
    e.preventDefault();
    if (this._suspended || !this._element) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const keyCode = this._touchPoints.get(touch.identifier);
      if (keyCode !== undefined) {
        this._touchPoints.delete(touch.identifier);
        this._release(keyCode);
      }
    }
  };

  private _handleTouchMove = (e: TouchEvent): void => {
    e.preventDefault();
    if (this._suspended || !this._element) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const keyCode = this._touchPoints.get(touch.identifier);
      if (keyCode === undefined) continue;
      const { x, y } = this._touchPos(touch);
      const hit = this._hitTest(x, y);
      if (hit && hit.keyCode !== keyCode) {
        this._touchPoints.set(touch.identifier, hit.keyCode);
        this._release(keyCode);
        this._press(hit.keyCode);
      } else if (!hit) {
        this._touchPoints.delete(touch.identifier);
        this._release(keyCode);
      }
    }
  };

  private _handleTouchCancel = (e: TouchEvent): void => {
    e.preventDefault();
    if (this._suspended || !this._element) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const keyCode = this._touchPoints.get(touch.identifier);
      if (keyCode !== undefined) {
        this._touchPoints.delete(touch.identifier);
        this._release(keyCode);
      }
    }
  };

  constructor(buttonDefs: ButtonDef[], scale: number) {
    this._buttonDefs = buttonDefs;
    this._scale = scale;
  }

  attach(element: HTMLElement): void {
    if (this._element) {
      this.detach();
    }
    this._element = element;
    element.addEventListener('touchstart', this._handleTouchStart, { passive: false });
    element.addEventListener('touchend', this._handleTouchEnd, { passive: false });
    element.addEventListener('touchmove', this._handleTouchMove, { passive: false });
    element.addEventListener('touchcancel', this._handleTouchCancel, { passive: false });
  }

  detach(): void {
    if (this._element) {
      this._element.removeEventListener('touchstart', this._handleTouchStart);
      this._element.removeEventListener('touchend', this._handleTouchEnd);
      this._element.removeEventListener('touchmove', this._handleTouchMove);
      this._element.removeEventListener('touchcancel', this._handleTouchCancel);
      this._element = null;
    }
    this._touchPoints.clear();
    this._pressedButtons.clear();
  }

  suspend(): void {
    this._suspended = true;
    const keyCodes = new Set(this._touchPoints.values());
    this._touchPoints.clear();
    this._pressedButtons.clear();
    for (const keyCode of keyCodes) {
      this._fire(keyCode, false);
    }
  }

  resume(): void {
    this._suspended = false;
  }

  private _touchPos(touch: Touch): { x: number; y: number } {
    const rect = this._element!.getBoundingClientRect();
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  }

  private _hitTest(x: number, y: number): ButtonDef | null {
    for (const def of this._buttonDefs) {
      const left = def.x * this._scale;
      const top = def.y * this._scale;
      const right = (def.x + def.w) * this._scale;
      const bottom = (def.y + def.h) * this._scale;
      if (x >= left && x < right && y >= top && y < bottom) {
        return def;
      }
    }
    return null;
  }

  private _press(keyCode: string): void {
    if (this._pressedButtons.has(keyCode)) return;
    this._pressedButtons.add(keyCode);
    this._fire(keyCode, true);
  }

  private _release(keyCode: string): void {
    if (!this._pressedButtons.has(keyCode)) return;
    for (const k of this._touchPoints.values()) {
      if (k === keyCode) return;
    }
    this._pressedButtons.delete(keyCode);
    this._fire(keyCode, false);
  }

  private _fire(keyCode: string, pressed: boolean): void {
    if (this.onInput) {
      this.onInput({ keyCode, scanCode: 0, pressed, timestamp: Date.now() });
    }
  }
}
